/**
 * SearchableSelect: combobox-style picker with text search.
 * Used for picking existing clients or services in dialogs.
 *
 * Props:
 *  - items: array of objects
 *  - value: id currently selected (or '')
 *  - onChange(item|null): called when user picks an option (or clears)
 *  - getKey(item): unique key, defaults to item.id
 *  - getLabel(item): main label shown in trigger / list
 *  - getSubLabel(item): optional secondary line in list
 *  - searchFields(item): array of strings searched against
 *  - placeholder, emptyLabel
 *  - testId: data-testid prefix
 */
import { useState, useMemo } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Check, ChevronsUpDown, Search, X } from 'lucide-react';
import { cn } from '../lib/utils';
import { normalizeText } from '../lib/clientSearch';
import useDebounce from '../hooks/useDebounce';

export default function SearchableSelect({
  items = [],
  value = '',
  onChange,
  getKey = (i) => i.id,
  getLabel = (i) => i.name || '',
  getSubLabel,
  searchFields = (i) => [i.name || ''],
  placeholder = 'Seleccionar...',
  emptyLabel = 'Sin resultados',
  allowClear = true,
  footer,
  onCloseChange,
  testId = 'searchable-select',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  // Debounced query: keeps the input snappy on lists with thousands of items
  // (e.g. the clients dropdown across POS, Agenda, Lealtad).
  const debouncedQuery = useDebounce(query, 300);

  const setOpenAndNotify = (next) => {
    setOpen(next);
    if (!next) setQuery('');
    if (onCloseChange) onCloseChange(next);
  };

  const selectedItem = useMemo(
    () => items.find((it) => getKey(it) === value) || null,
    [items, value, getKey]
  );

  const filtered = useMemo(() => {
    const q = normalizeText(debouncedQuery);
    if (!q) return items;
    // Multi-token AND: every token must be found in ANY of the search fields.
    const tokens = q.split(/\s+/).filter(Boolean);
    return items.filter((it) => {
      const fields = searchFields(it).map((f) => normalizeText(f));
      const haystack = fields.join(' | ');
      return tokens.every((tok) => haystack.includes(tok));
    });
  }, [items, debouncedQuery, searchFields]);

  return (
    <Popover open={open} onOpenChange={setOpenAndNotify}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between bg-white border-slate-200 font-normal"
          data-testid={`${testId}-trigger`}
        >
          <span className={cn('truncate', !selectedItem && 'text-slate-400')}>
            {selectedItem ? getLabel(selectedItem) : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-[--radix-popover-trigger-width]"
        align="start"
        data-testid={`${testId}-content`}
      >
        <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2">
          <Search className="h-4 w-4 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar..."
            className="h-8 border-0 shadow-none focus-visible:ring-0 px-0"
            data-testid={`${testId}-input`}
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="text-slate-400 hover:text-slate-700"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <ScrollArea className="max-h-64">
          {filtered.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-slate-400">
              {emptyLabel}
            </div>
          ) : (
            <ul className="py-1" data-testid={`${testId}-list`}>
              {filtered.slice(0, 1000).map((it) => {
                const k = getKey(it);
                const isActive = k === value;
                return (
                  <li key={k}>
                    <button
                      type="button"
                      onClick={() => {
                        onChange(it);
                        setOpen(false);
                        setQuery('');
                      }}
                      className={cn(
                        'flex w-full items-start gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50 transition-colors',
                        isActive && 'bg-slate-100'
                      )}
                      data-testid={`${testId}-option-${k}`}
                    >
                      <Check
                        className={cn(
                          'mt-0.5 h-4 w-4 shrink-0',
                          isActive ? 'opacity-100 text-primary' : 'opacity-0'
                        )}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium text-slate-900">
                          {getLabel(it)}
                        </div>
                        {getSubLabel && (
                          <div className="truncate text-xs text-slate-500">
                            {getSubLabel(it)}
                          </div>
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
              {filtered.length > 1000 && (
                <li className="px-3 py-2 text-xs text-slate-400 text-center border-t border-slate-100">
                  Mostrando 1000 de {filtered.length}. Refina tu búsqueda.
                </li>
              )}
            </ul>
          )}
          {allowClear && selectedItem && (
            <button
              type="button"
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
              className="w-full border-t border-slate-100 px-3 py-2 text-left text-xs text-slate-500 hover:bg-slate-50"
              data-testid={`${testId}-clear`}
            >
              Limpiar selección
            </button>
          )}
        </ScrollArea>
        {footer && (
          <div
            className="border-t border-slate-200 bg-slate-50/50"
            data-testid={`${testId}-footer`}
          >
            {typeof footer === 'function' ? footer({ close: () => setOpenAndNotify(false) }) : footer}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
